// utils/analytics.js
// Утилиты для работы с Google Analytics

/**
 * Получает Google Analytics client ID из различных источников
 * Приоритет:
 * 1. Cookie _ga
 * 2. Cookie _ga_XXX (GA4)
 * 3. gtag API
 * 4. Universal Analytics API (устаревший)
 * 
 * @param {number} timeout - Таймаут в мс для ожидания ответа от API
 * @returns {Promise<string|null>} - Google Analytics client ID или null, если не удалось получить
 */
export const getGAClientId = (timeout = 2000) => {
    return new Promise((resolve) => {
      console.log("Attempt to get GA Client ID");
      
      // Устанавливаем таймаут, чтобы не ждать вечно
      const timeoutId = setTimeout(() => {
        console.log("GA Client ID timeout");
        resolve(null);
      }, timeout);
      
      // 1. СНАЧАЛА проверяем cookie - это самый быстрый и надежный метод
      const getIdFromCookie = () => {
        try {
          // GA4 cookie format: _ga=GA1.1.1234567890.1234567890
          // или _ga=GA1.2.1234567890.1234567890
          const cookies = document.cookie.split('; ');
          
          // Ищем _ga cookie
          const gaCookie = cookies.find(row => row.startsWith('_ga='));
          if (gaCookie) {
            console.log("Found _ga cookie:", gaCookie);
            // Извлекаем ID после "GA1.X."
            const parts = gaCookie.split('.');
            if (parts.length >= 4) {
              const clientId = `${parts[2]}.${parts[3]}`;
              console.log("Extracted GA client_id from cookie:", clientId);
              return clientId;
            }
          }
          
          // Проверяем также _ga_XXXXXXXX cookie (специфично для GA4)
          const ga4Cookie = cookies.find(row => row.startsWith('_ga_'));
          if (ga4Cookie) {
            console.log("Found GA4 cookie:", ga4Cookie);
            // GA4 specific cookie contains session data
            // Format: _ga_XXXXXXXX=GS1.1.1234567890.1.1.1234567890.0.0.0
            const ga4parts = ga4Cookie.split('.');
            if (ga4parts.length >= 3) {
              // Используем timestamp как client_id если нет другого
              const clientId = ga4parts[2];
              console.log("Extracted client_id from GA4 cookie:", clientId);
              return clientId;
            }
          }
        } catch (e) {
          console.error("Error parsing cookies:", e);
        }
        return null;
      };
  
      // Сначала пробуем получить ID из cookie (это работает мгновенно)
      const cookieClientId = getIdFromCookie();
      if (cookieClientId) {
        clearTimeout(timeoutId);
        resolve(cookieClientId);
        return;
      }
      
      // 2. Если cookie не найден, пробуем gtag API
      if (typeof window !== 'undefined' && window.gtag) {
        try {
          console.log("Trying gtag API with:", process.env.NEXT_PUBLIC_GA4_MEASUREMENT_ID);
          window.gtag('get', process.env.NEXT_PUBLIC_GA4_MEASUREMENT_ID, 'client_id', (clientId) => {
            if (clientId) {
              console.log("Successfully got client_id from gtag:", clientId);
              clearTimeout(timeoutId);
              resolve(clientId);
            } else {
              console.log("gtag returned null client_id");
              // Не делаем resolve здесь, продолжаем проверку других методов
            }
          });
        } catch (e) {
          console.error("Error using gtag API:", e);
          // Продолжаем выполнение - может сработает другой метод
        }
      } 
      // 3. Пробуем Universal Analytics (устаревший, но может быть доступен)
      else if (typeof window !== 'undefined' && window.ga) {
        try {
          console.log("Trying Universal Analytics API");
          const tracker = window.ga.getAll()[0];
          if (tracker) {
            const clientId = tracker.get('clientId');
            console.log("Got client_id from UA tracker:", clientId);
            clearTimeout(timeoutId);
            resolve(clientId);
            return;
          }
        } catch (e) {
          console.error("Error with UA API:", e);
          // Продолжаем выполнение
        }
      }
  
      // Если мы дошли сюда и timeout еще не сработал, значит мы все еще ждем ответа от gtag API
      // Timeout очистит себя и вернет null если ничего не найдено
    });
  };
  
  /**
   * Собирает метаданные для передачи в Stripe, включая GA client ID
   * 
   * @param {string|null} ga_client_id - Google Analytics client ID
   * @param {Object} userState - Состояние пользователя из useUserStore
   * @param {string} currentPage - Текущая страница
   * @returns {Object} - Объект с метаданными
   */
  export const collectMetadata = async (ga_client_id = null, userState = null, currentPage = '') => {
    // Если ga_client_id не передан, пытаемся получить его
    if (!ga_client_id) {
      ga_client_id = await getGAClientId();
    }
    
    // Получаем состояние пользователя, если не передано
    if (!userState && typeof window !== 'undefined') {
      try {
        // Проверяем, доступен ли useUserStore глобально
        if (window.useUserStore && typeof window.useUserStore.getState === 'function') {
          userState = window.useUserStore.getState();
        }
      } catch (e) {
        console.error("Error accessing userStore:", e);
      }
    }
    
    // Собираем базовые метаданные
    const metadata = {
      landing_page: currentPage || (typeof window !== 'undefined' ? window.location.pathname : ''),
      utm_source: typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('utm_source') || '' : '',
      utm_medium: typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('utm_medium') || '' : '',
      utm_campaign: typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('utm_campaign') || '' : '',
    };
    
    // Добавляем client_id, если есть
    if (ga_client_id) {
      metadata.ga_client_id = ga_client_id;
    }
    
    // Добавляем данные пользователя, если доступны (email не передаем)
    if (userState && userState.user) {
      metadata.user_id = userState.user.id || '';
      metadata.user_source = userState.user.source || 'website';
    }
    
    return metadata;
  };
  
  /**
   * Отправляет событие в Google Analytics через dataLayer
   * 
   * @param {string} eventName - Название события
   * @param {Object} eventData - Данные события
   * @returns {boolean} - true, если событие отправлено успешно
   */
  export const sendGAEvent = (eventName, eventData = {}) => {
    if (typeof window === 'undefined' || !window.dataLayer) {
      console.error("dataLayer not available");
      return false;
    }
    
    try {
      // Добавляем название события в данные
      const fullEventData = {
        event: eventName,
        ...eventData
      };
      
      // Отправляем событие
      window.dataLayer.push(fullEventData);
      console.log(`GA event '${eventName}' sent:`, fullEventData);
      return true;
    } catch (e) {
      console.error(`Error sending GA event '${eventName}':`, e);
      return false;
    }
  };
  
  /**
   * Полностью подготавливает и отправляет событие begin_checkout с товарами из корзины
   * и собранными метаданными
   * 
   * @param {Array} cartItems - Массив товаров в корзине
   * @param {number} totalPrice - Общая стоимость корзины
   * @returns {Promise<Object>} - Объект с метаданными и статусом отправки события
   */
  export const prepareCheckoutEvent = async (cartItems, totalPrice) => {
    // Получаем GA client ID
    const ga_client_id = await getGAClientId();
    
    // Подготавливаем данные о товарах для события
    const items = (cartItems || []).map(item => ({
      item_id: item.slug || '',
      item_name: item.slug || '',
      price: (item.product && typeof item.product.price === 'number') ? item.product.price : 0
    }));
    
    // Создаем данные события
    const eventData = {
      cartValue: totalPrice,     // Для GTM переменной {{cartValue}}
      cart_value: totalPrice,    // Стандартное имя в GA4
      items: items,              // Для GTM переменной {{items}}
      client_id: ga_client_id    // Добавляем client_id в событие
    };
    
    // Отправляем событие
    const eventSent = sendGAEvent('begin_checkout', eventData);
    
    // Собираем метаданные для Stripe
    const metadata = await collectMetadata(ga_client_id);
    
    return {
      metadata,
      eventSent,
      ga_client_id
    };
  };
  
  export default {
    getGAClientId,
    collectMetadata,
    sendGAEvent,
    prepareCheckoutEvent
  };